import React from 'react';
import type { FC } from 'react';

import { useEnforceAllowedLocale } from '@modernloop/shared/feature-flag';
import { ScrollReset } from '@modernloop/shared/helper-components';
import { CookieConsentProvider } from '@modernloop/shared/hooks';
import { ThemeProvider } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { SnackbarProvider } from 'notistack';

import { AutoRefreshingRouter } from 'src/components/AutoRefreshingRouter';
import Toast from 'src/components/Toast';

import { ColorSchemeProvider } from 'src/contexts/ColorSchemeProvider';
import { DisplayTimezoneProvider } from 'src/contexts/DisplayTimezoneProvider';
import LDProviderWrapper from 'src/contexts/LDProviderWrapper';
import { LastUsedTimezonesProvider } from 'src/contexts/LastUsedTimezonesProvider';
import { LastUsedZoomUserIdsProvider } from 'src/contexts/LastUsedZoomUserIdsProvider';
import { SidebarStateProvider } from 'src/contexts/SidebarStateProvider';
import { AuthProvider } from 'src/contexts/auth0/Auth0Context';

import { useConnectionMonitor } from 'src/hooks/useConnectionMonitor';

import { AnalyticsServices } from 'src/views-new/AnalyticsServices';

import GlobalStyles from 'src/shared/app/GlobalStyles';
import IdentifyLoggers from 'src/shared/app/IdentifyLoggers';
import createTheme from 'src/themeMui5';

import { LocalhostBanner } from '../components/base/LocalhostBanner';

/**
 * Enforces that the active locale is allowed by the feature flag.
 * Must be rendered inside LDProviderWrapper.
 */
const EnforceLocale: FC<{ children?: React.ReactNode }> = ({ children }) => {
  useEnforceAllowedLocale();
  return <>{children}</>;
};

/** Monitors backend connectivity and shows a toast when the connection is lost. */
const ConnectionMonitor: FC = () => {
  useConnectionMonitor();
  return null;
};

/**
 * The component that wraps the entire application for authenticated users.
 * This component sets up the theme, and provides the context providers for the application.
 */
const BaseApp: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const authTheme = createTheme();

  return (
    <AutoRefreshingRouter>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ThemeProvider theme={authTheme}>
          <GlobalStyles />
          <AuthProvider>
            <LDProviderWrapper>
              <ColorSchemeProvider>
                <SnackbarProvider
                  TransitionProps={{ direction: 'up' }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  Components={{ default: Toast, error: Toast, info: Toast, success: Toast, warning: Toast }}
                  dense
                  maxSnack={3}
                >
                  <ConnectionMonitor />
                  <ScrollReset />
                  <EnforceLocale>
                    <CookieConsentProvider>
                      <DisplayTimezoneProvider>
                        <LastUsedTimezonesProvider>
                          <LastUsedZoomUserIdsProvider>
                            <SidebarStateProvider>
                              <AnalyticsServices />
                              <IdentifyLoggers />
                              <LocalhostBanner />
                              {children}
                            </SidebarStateProvider>
                          </LastUsedZoomUserIdsProvider>
                        </LastUsedTimezonesProvider>
                      </DisplayTimezoneProvider>
                    </CookieConsentProvider>
                  </EnforceLocale>
                </SnackbarProvider>
              </ColorSchemeProvider>
            </LDProviderWrapper>
          </AuthProvider>
        </ThemeProvider>
      </LocalizationProvider>
    </AutoRefreshingRouter>
  );
};

export default BaseApp;
